'use client'

import { useState, useEffect } from 'react'
import { Bookmark } from 'lucide-react'
import { toggleSavePost, checkIsSaved } from '@/lib/user-actions'
import { useAuth, SignInButton } from '@clerk/nextjs'

interface SaveButtonProps {
  postId: string
  initialIsSaved?: boolean // Keep optional for backwards compatibility
}

export default function SaveButton({ postId, initialIsSaved = false }: SaveButtonProps) {
  const { isLoaded, userId } = useAuth()
  const [isSaved, setIsSaved] = useState(initialIsSaved)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let mounted = true;
    if (isLoaded && userId) {
      checkIsSaved(userId, postId).then((saved) => {
        if (mounted) {
          setIsSaved(saved);
          setIsLoading(false);
        }
      });
    } else if (isLoaded && !userId) {
      setIsLoading(false);
    }
    return () => { mounted = false; }
  }, [isLoaded, userId, postId])

  const handleToggle = async () => {
    if (!isLoaded || !userId) return
    
    // Optimistic Update
    const previousState = isSaved
    setIsSaved(!previousState)
    setIsLoading(true)

    try {
      const result = await toggleSavePost(userId, postId)
      if (!result.success) {
        // Rollback on failure
        setIsSaved(previousState)
      } else {
        setIsSaved(result.isSaved || false)
      }
    } catch (error) {
      setIsSaved(previousState)
    } finally {
      setIsLoading(false)
    }
  }

  // Guest State: Show a trigger for Login
  if (isLoaded && !userId) {
    return (
      <SignInButton mode="modal">
        <button 
          title="Login to save for later"
          className="w-10 h-10 rounded-full border border-border bg-background hover:bg-brand/10 transition-all flex items-center justify-center group"
        >
          <Bookmark className="w-4 h-4 text-muted-foreground group-hover:text-brand" />
        </button>
      </SignInButton>
    )
  }

  return (
    <button 
      onClick={handleToggle}
      disabled={isLoading}
      title={isSaved ? "Remove from bureau" : "Save to bureau"}
      className={`w-10 h-10 rounded-full border transition-all flex items-center justify-center group ${
        isSaved 
          ? 'bg-brand border-brand text-white shadow-lg' 
          : 'bg-background border-border hover:border-brand/50 hover:bg-brand/5 text-muted-foreground hover:text-brand'
      } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <Bookmark 
        className={`w-4 h-4 transition-transform duration-300 ${isSaved ? 'scale-110 fill-white' : 'group-hover:scale-110'}`} 
      />
    </button>
  )
}
